import { z } from 'zod';

export const ECONOMY_RESEARCH_HTTP_CONTRACT_VERSION = 'economy-research-v1' as const;

export const ResearchRunStatusSchema = z.enum([
  'requested',
  'dispatched',
  'queued',
  'running',
  'ready',
  'failed',
  /**
   * Withdrawn by a person (or superseded). Terminal, and deliberately distinct from `failed`:
   * nothing went wrong, so it must not read as an error or invite a retry (ADR 0104 §4).
   */
  'cancelled',
]);

/**
 * What a run is *about*. A `product` is one row in the workspace; an `objective` is a whole
 * market from the playbook (A — Global & U.S. AI B2C, B — Saudi Arabia AI B2C), which is what
 * the Market Research view's "AI search" control researches.
 */
/** Every sweep kind is a research subject (ADR 0104 §3) — one capability, a typed scope. */
export const RESEARCH_SUBJECT_TYPES = [
  'product',
  'objective',
  'category',
  'alternatives',
  'demand',
  'keyword',
  'prompt',
] as const;

export const ResearchSubjectSchema = z.object({
  type: z.enum(RESEARCH_SUBJECT_TYPES),
  id: z.string().min(1),
  /** How the board names it. Free text, so the UI never has to reverse-engineer a label from an id. */
  label: z.string().max(200).optional(),
  /** The rest of the sweep's scope map (family, query, signalId, …). */
  params: z.record(z.string().max(200)).optional(),
});
export type ResearchSubject = z.infer<typeof ResearchSubjectSchema>;

export const ResearchRunSchema = z.object({
  id: z.string().min(1),
  ownerId: z.string().min(1),
  subject: ResearchSubjectSchema,
  capability: z.literal('economy.product-research.v1'),
  contextManifestRef: z.string().min(1),
  status: ResearchRunStatusSchema,
  requestKey: z.string().min(1),
  createdAt: z.number().int().nonnegative(),
  dispatchedAt: z.number().int().nonnegative().nullable(),
  /**
   * The Agent Runner run fulfilling this request, once one has picked it up. Null means there is
   * genuinely no step log yet — which is what lets the drawer say so instead of showing an empty
   * list that reads as "the agent did nothing" (ADR 0104 §2).
   */
  agentRunId: z.string().min(1).nullable(),
  /**
   * Set when the fulfiller reports a result. The run stays `running` until the Gold manifest
   * is validated and projected (ADR 0099 §10 — `ready` means projected, not merely finished),
   * so this is what tells the UI "the agent is done, the projection is not" (ECONOMY-10 G10).
   */
  resultRef: z.string().min(1).nullable(),
  /** Populated on `failed` so the UI can show the analyst why, not just that. */
  error: z.string().min(1).nullable(),
  /**
   * Progress, projected from the agent's plan + milestone facts (ADR 0104 §2). Milestone
   * granularity on purpose: "step 3 of 7 · collecting evidence" is a claim the system can support,
   * unlike a percentage. Absent until the agent publishes a plan.
   */
  steps: z.array(z.string()).nullable(),
  stepIndex: z.number().int().nonnegative().nullable(),
  milestone: z.string().nullable(),
  counts: z.object({
    found: z.number().int().nonnegative(),
    duplicates: z.number().int().nonnegative(),
    known: z.number().int().nonnegative(),
  }).nullable(),
  /**
   * What the Gold→board projection actually wrote (ECONOMY-10 N28 / ADR 0099 §10). Null until the
   * run is `ready`, because before that nothing has been projected and a zero would be a lie.
   *
   * `unkeyed` is carried beside `products` on purpose: the agent refuses to guess a product's
   * identity (ADR 0104 §5), so candidates it could not key are dropped from the board — and a drop
   * nobody can see is indistinguishable from a candidate that was never found.
   */
  projected: z.object({
    products: z.number().int().nonnegative(),
    unkeyed: z.number().int().nonnegative(),
    /**
     * Ids of the written products the board ALREADY held, which therefore kept the round that
     * first found them (ECONOMY-19 I3). This is what lets a re-run's round show only genuinely
     * new finds while still accounting for the rest — "also found, already on your board: N".
     *
     * Optional on purpose: a run projected before I4 shipped carries no list, and rendering that
     * as `0` would claim a re-run found everything fresh. Absent means unknown, not empty.
     */
    refound: z.array(z.string()).optional(),
  }).nullable(),
  /**
   * A caveat about the result as a whole, from the agent's manifest — e.g. "the landed sources
   * carry no geography field, so this regional objective can only match a text mention". Shown
   * against a `ready` run so an empty-but-successful result explains itself.
   */
  note: z.string().nullable(),
});
export type ResearchRun = z.infer<typeof ResearchRunSchema>;

/** Client-generated requestKey makes repeated Start Research actions idempotent. */
export const StartResearchRequestSchema = z.object({
  ownerId: z.string().min(1),
  subject: ResearchSubjectSchema,
  requestKey: z.string().min(1).max(128),
});
export type StartResearchRequest = z.infer<typeof StartResearchRequestSchema>;

export const StartResearchResponseSchema = z.object({
  run: ResearchRunSchema,
  created: z.boolean(),
});
export type StartResearchResponse = z.infer<typeof StartResearchResponseSchema>;

export const ResearchRunListResponseSchema = z.object({
  runs: z.array(ResearchRunSchema),
});
export type ResearchRunListResponse = z.infer<typeof ResearchRunListResponseSchema>;

export const ResearchRelayResponseSchema = z.object({
  published: z.number().int().nonnegative(),
  failed: z.number().int().nonnegative(),
});
export type ResearchRelayResponse = z.infer<typeof ResearchRelayResponseSchema>;

/** Cancel a run in flight. `reason` separates a person's decision from an automatic supersede. */
export const CancelResearchRequestSchema = z.object({
  ownerId: z.string().min(1),
  reason: z.enum(['operator', 'superseded']).default('operator'),
  note: z.string().max(280).optional(),
});
export type CancelResearchRequest = z.infer<typeof CancelResearchRequestSchema>;

export const CancelResearchResponseSchema = z.object({
  run: ResearchRunSchema,
  /** False when the run was already terminal — cancelling twice is a no-op, not an error. */
  cancelled: z.boolean(),
});
export type CancelResearchResponse = z.infer<typeof CancelResearchResponseSchema>;

/**
 * One line of the agent's step log — detail fetched **on demand** when an analyst opens the
 * drawer, never streamed across the Event Hub (ADR 0104 §2). `seq` is the agent's own per-run
 * sequence number, so the drawer orders by the producer's truth rather than by clock skew.
 */
export const ResearchLogEntrySchema = z.object({
  seq: z.number().int().nonnegative(),
  at: z.number().int().nonnegative(),
  kind: z.string().min(1),
  /** Already flattened to text by the API — the drawer renders, it does not interpret. */
  text: z.string(),
});
export type ResearchLogEntry = z.infer<typeof ResearchLogEntrySchema>;

export const ResearchRunLogResponseSchema = z.object({
  runId: z.string().min(1),
  /** Null when the run has not been picked up yet — there is genuinely no agent log to show. */
  agentRunId: z.string().min(1).nullable(),
  entries: z.array(ResearchLogEntrySchema),
});
export type ResearchRunLogResponse = z.infer<typeof ResearchRunLogResponseSchema>;
